import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { calculateBeyondTestResults } from '../../utils/beyondTestLogic';
import { saveBeyondResult, getResultDetail } from '../../client';
import { useContent } from '../../content';

const Result = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { getC, loaded: contentLoaded } = useContent(['beyond']);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (id) {
      if (id.startsWith('guest_')) {
        const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
        const guestResult = pending.find(item => item.payload?.id === id);
        if (guestResult && guestResult.payload) {
          setResult(guestResult.payload.payload || guestResult.payload);
        } else {
          navigate('/mypage');
        }
        return;
      }

      const loadHistory = async () => {
        const data = await getResultDetail(id);
        if (data && data.payload) {
          setResult(data.payload);
        } else {
          const pending = JSON.parse(localStorage.getItem('pendingResults') || '[]');
          const guestResult = pending.find(item => item.payload?.id === id);
          if (guestResult && guestResult.payload) {
             setResult(guestResult.payload.payload || guestResult.payload);
          } else {
             navigate('/mypage');
          }
        }
      };
      loadHistory();
      return;
    }

    const savedAnswers = localStorage.getItem('beyondTestAnswers');
    if (!savedAnswers) {
      navigate('/beyond-test/q/1');
      return;
    }

    const answers = JSON.parse(savedAnswers);
    const calculated = calculateBeyondTestResults(answers);
    setResult(calculated);

    // Save to backend
    const saveResult = async () => {
      const sLabel = getC(`beyond.beyond_test.results.S.${calculated.S}.title`);
      const eLabel = getC(`beyond.beyond_test.results.E.${calculated.E}.title`);
      const bLabel = getC(`beyond.beyond_test.results.B.${calculated.B}.title`);

      const resultLabel = `${calculated.S}${calculated.E}${calculated.B}`;

      const summary = {
        S: calculated.S,
        E: calculated.E,
        B: calculated.B,
        labels: { S: sLabel, E: eLabel, B: bLabel },
        scores: calculated.scores
      };

      const dataToSave = {
        result_label: resultLabel, // Top-level for routing/indexing
        summary: summary,          // Top-level summary
        payload: {                 // Nested payload for internal details
          result_key: resultLabel,
          ...calculated,
          answers
        }
      };

      console.log("Attempting to save Beyond Test result:", dataToSave);
      try {
        await saveBeyondResult(dataToSave);
        console.log("Beyond Test result saved successfully!");
      } catch (e) {
        console.error("Failed to save beyond result", e);
      }
    };

    if (contentLoaded) {
      saveResult();
    }

  }, [navigate, id, contentLoaded]);

  if (!result || !contentLoaded) return <div />;

  const getResContent = (axis, type) => getC(`beyond.beyond_test.results.${axis}.${type}`);
  const formatSectionText = (value) => {
    const text = String(value || '').replace(/\s+/g, ' ').trim();
    if (!text) return '';
    const sentences = text
      .split(/(?<=[.!?])\s+|(?<=다\.)\s+/)
      .map((s) => s.trim())
      .filter(Boolean);
    if (sentences.length <= 1) return text;
    return sentences.join('\n');
  };

  const sData = getResContent('S', result.S);
  const eData = getResContent('E', result.E);
  const bData = getResContent('B', result.B);
  const axisCards = [
    { key: 'S', badge: 'S-Ideal', title: sData.title, desc: sData.desc, color: '#fbbf24' },
    { key: 'E', badge: 'E-Ideal', title: eData.title, desc: eData.desc, color: '#60a5fa' },
    { key: 'B', badge: 'B-Ideal', title: bData.title, desc: bData.desc, color: '#f472b6' }
  ];
  const scoreSafe = result.scores || { S: 50, E: 50, B: 50 };

  return (
    <div className="beyond-test-container">
      <div className="result-card">
        <div className="result-header">
          <p className="type-label">Beyond Test Result</p>
          <h1 className="result-title">{result.S}{result.E}{result.B}</h1>
          <p className="header-help">나의 이상형 좌표와 축별 해석을 한눈에 확인하세요.</p>
        </div>

        <section className="score-overview">
          {[
            { key: 'S', label: 'S', color: '#fbbf24' },
            { key: 'E', label: 'E', color: '#60a5fa' },
            { key: 'B', label: 'B', color: '#f472b6' }
          ].map((item) => (
            <div key={item.key} className="score-item">
              <div className="score-row">
                <span>{item.label}</span>
                <strong>{Math.round(scoreSafe[item.key] ?? 50)}%</strong>
              </div>
              <div className="score-track">
                <div className="score-fill" style={{ width: `${Math.round(scoreSafe[item.key] ?? 50)}%`, background: item.color }} />
              </div>
            </div>
          ))}
        </section>

        <div className="result-sections">
          {axisCards.map((axis) => (
            <section className="result-section" key={axis.key}>
              <span className="axis-badge" style={{ color: axis.color, borderColor: `${axis.color}66` }}>{axis.badge}</span>
              <h3>{axis.title}</h3>
              <p>{formatSectionText(axis.desc)}</p>
            </section>
          ))}
        </div>

        <div className="action-buttons">
          <button className="retry-btn" onClick={() => {
            localStorage.removeItem('beyondTestAnswers');
            navigate('/beyond-test/q/1');
          }}>
            분석 다시하기
          </button>
          <button className="save-btn" onClick={() => navigate('/mypage')}>
            마이페이지 저장하기
          </button>
          <button className="share-btn" onClick={async () => {
            const shareText = `비욘드 테스트 결과: ${result.S}${result.E}${result.B}`;
            try {
              if (navigator.share) {
                await navigator.share({ title: 'STARTUS 비욘드 테스트 결과', text: shareText, url: window.location.href });
              } else {
                await navigator.clipboard?.writeText(`${shareText} ${window.location.href}`);
                alert('결과 링크가 복사되었습니다.');
              }
            } catch (e) {
              console.warn('Beyond result share cancelled or failed:', e);
            }
          }}>
            결과 공유하기
          </button>
        </div>
      </div>

      <style jsx>{`
        .beyond-test-container {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 1.6rem 1rem;
          background: radial-gradient(circle at 10% 0%, #1e293b 0%, #0b1220 45%, #050816 100%);
          color: white;
        }

        .result-card {
          background: rgba(15, 23, 42, 0.78);
          backdrop-filter: blur(8px);
          border: 1px solid rgba(148, 163, 184, 0.22);
          border-radius: 18px;
          padding: 1.35rem;
          width: 100%;
          max-width: 900px;
          box-shadow: 0 18px 38px rgba(2, 6, 23, 0.45);
          animation: slideUp 0.6s ease-out;
        }

        .result-header {
          text-align: center;
          margin-bottom: 1rem;
        }

        .type-label {
          color: #94a3b8;
          font-size: 0.85rem;
          margin-bottom: 0.35rem;
          text-transform: uppercase;
          letter-spacing: 0.12em;
        }

        .result-title {
          font-size: 2.9rem;
          font-weight: 800;
          line-height: 1.2;
          background: linear-gradient(to right, #fbbf24, #60a5fa);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          margin: 0;
          letter-spacing: 0.25em;
        }

        .header-help {
          margin: 0.45rem 0 0 0;
          color: #cbd5e1;
          font-size: 0.88rem;
        }

        .score-overview {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.7rem;
          margin-bottom: 1rem;
        }

        .score-item {
          background: rgba(255, 255, 255, 0.03);
          border: 1px solid rgba(148, 163, 184, 0.2);
          border-radius: 10px;
          padding: 0.7rem 0.75rem;
        }

        .score-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.82rem;
          color: #e2e8f0;
          margin-bottom: 0.35rem;
        }

        .score-track {
          width: 100%;
          height: 6px;
          border-radius: 999px;
          background: rgba(255, 255, 255, 0.12);
          overflow: hidden;
        }

        .score-fill {
          height: 100%;
          border-radius: 999px;
        }

        .result-sections {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 0.75rem;
          margin-bottom: 1.1rem;
        }

        .result-section {
          background: rgba(255, 255, 255, 0.03);
          padding: 0.9rem;
          border-radius: 12px;
          border: 1px solid rgba(148, 163, 184, 0.2);
        }
        
        .axis-badge {
            display: inline-block;
            font-size: 0.72rem;
            font-weight: bold;
            background: rgba(15, 23, 42, 0.6);
            padding: 0.2rem 0.6rem;
            border-radius: 99px;
            margin-bottom: 0.45rem;
            border: 1px solid rgba(148, 163, 184, 0.25);
        }

        .result-section h3 {
          font-size: 0.98rem;
          color: #e2e8f0;
          margin-bottom: 0.4rem;
          font-weight: 700;
          line-height: 1.35;
        }

        .result-section p {
          color: #cbd5e1;
          line-height: 1.5;
          font-size: 0.86rem;
          margin: 0;
          white-space: pre-line;
        }

        .action-buttons {
          display: flex;
          gap: 1rem;
          margin-top: 0.3rem;
        }

        .retry-btn, .save-btn, .share-btn {
          flex: 1;
          padding: 0.85rem;
          border-radius: 10px;
          font-weight: 600;
          cursor: pointer;
          transition: transform 0.2s, opacity 0.2s;
        }

        .retry-btn {
           background: linear-gradient(to right, #8b5cf6, #ec4899);
           border: none;
           color: white;
        }

        .save-btn, .share-btn {
          background: transparent;
          border: 1px solid rgba(255,255,255,0.2);
          color: white;
        }

        .retry-btn:hover, .save-btn:hover, .share-btn:hover {
          transform: translateY(-2px);
        }

        @media (max-width: 640px) {
          .action-buttons {
            flex-direction: column;
          }
        }

        @media (max-width: 860px) {
          .score-overview {
            grid-template-columns: 1fr;
          }
          .result-sections {
            grid-template-columns: 1fr;
          }
          .result-title {
            font-size: 2.3rem;
          }
        }

        @keyframes slideUp {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default Result;
